import { useState, useEffect } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { tdsAPI } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Export, Plus, CurrencyInr } from '@phosphor-icons/react';

export default function TDSPage() {
  const [loading, setLoading] = useState(true);
  const [quarter, setQuarter] = useState('Q4');
  const [year, setYear] = useState('2025');
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [entriesRes, summaryRes] = await Promise.all([
        tdsAPI.listEntries({}),
        tdsAPI.getSummary(quarter, year),
      ]);
      setEntries(entriesRes.data.entries || []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to load TDS data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [quarter, year]);

  const getSectionBadgeColor = (section) => {
    switch(section) {
      case '194C': return 'bg-blue-100 text-blue-700';
      case '194J': return 'bg-purple-100 text-purple-700';
      case '194H': return 'bg-amber-100 text-amber-700';
      case '194I': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div data-testid="tds-page">
      <PageHeader 
        title="TDS Management" 
        subtitle="Track TDS deductions and generate challan data"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" data-testid="add-tds-btn">
              <Plus size={16} className="mr-2" />
              Add Entry
            </Button>
            <Button className="bg-[#0F4C81] hover:bg-[#0B3C68]" data-testid="export-tds-btn">
              <Export size={16} className="mr-2" />
              Export Challan
            </Button>
          </div>
        }
      />
      
      <div className="page-content">
        {/* Period Selection */}
        <div className="flex items-center gap-4 mb-6">
          <Select value={quarter} onValueChange={setQuarter}>
            <SelectTrigger className="w-32" data-testid="quarter-select">
              <SelectValue placeholder="Quarter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Q1">Q1 (Apr-Jun)</SelectItem>
              <SelectItem value="Q2">Q2 (Jul-Sep)</SelectItem>
              <SelectItem value="Q3">Q3 (Oct-Dec)</SelectItem>
              <SelectItem value="Q4">Q4 (Jan-Mar)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-32" data-testid="year-select">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2025">FY 2025-26</SelectItem>
              <SelectItem value="2024">FY 2024-25</SelectItem>
              <SelectItem value="2023">FY 2023-24</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="summary" className="space-y-6">
          <TabsList>
            <TabsTrigger value="summary" data-testid="tab-summary">Summary</TabsTrigger>
            <TabsTrigger value="entries" data-testid="tab-entries">All Entries</TabsTrigger>
          </TabsList>

          <TabsContent value="summary">
            <div className="grid grid-cols-3 gap-6">
              <div className="stats-card col-span-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-[#E2E8F0]">
                    <CurrencyInr size={20} className="text-[#0F4C81]" />
                  </div>
                  <div>
                    <p className="stats-card-label">Total TDS</p>
                    <p className="stats-card-value">{formatCurrency(summary?.total_tds || 0)}</p>
                  </div>
                </div>
                <p className="text-xs text-[#64748B]">{quarter} {year}</p>
              </div>
            </div>

            {/* By Section */}
            <div className="mt-6 bg-white border border-[#E2E8F0] rounded-lg">
              <div className="p-4 border-b border-[#E2E8F0]">
                <h3 className="font-medium font-['Plus_Jakarta_Sans']">TDS by Section</h3>
              </div>
              <div className="p-4">
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="skeleton h-16 rounded-lg"></div>
                    ))}
                  </div>
                ) : summary?.by_section && Object.keys(summary.by_section).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(summary.by_section).map(([section, data]) => (
                      <div key={section} className="p-4 bg-[#F8FAFC] rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${getSectionBadgeColor(section)}`}>
                              {section}
                            </span>
                            <span className="text-sm text-[#64748B]">{data.count} deductions</span>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-[#64748B]">Payment: {formatCurrency(data.payment_amount)}</p>
                            <p className="font-semibold">TDS: {formatCurrency(data.tds_amount)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#64748B]">
                    <p>No TDS data for this period</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="entries">
            <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th>PAN</th>
                    <th>Section</th>
                    <th>Payment Date</th>
                    <th>Payment Amount</th>
                    <th>TDS Rate</th>
                    <th>TDS Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(10)].map((_, i) => (
                      <tr key={i}>
                        {[...Array(8)].map((_, j) => (
                          <td key={j}><div className="skeleton h-4 w-16 rounded"></div></td>
                        ))}
                      </tr>
                    ))
                  ) : entries.length > 0 ? (
                    entries.map((entry) => (
                      <tr key={entry.id} data-testid={`tds-row-${entry.id}`}>
                        <td className="font-medium">{entry.vendor_name}</td>
                        <td className="font-mono text-sm">{entry.vendor_pan}</td>
                        <td>
                          <span className={`text-xs px-2 py-1 rounded-full ${getSectionBadgeColor(entry.section)}`}>
                            {entry.section}
                          </span>
                        </td>
                        <td>{formatDate(entry.payment_date)}</td>
                        <td>{formatCurrency(entry.payment_amount)}</td>
                        <td>{entry.tds_rate}%</td>
                        <td className="font-medium">{formatCurrency(entry.tds_amount)}</td>
                        <td>
                          <span className={`status-pill ${entry.status}`}>{entry.status}</span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-[#64748B]">
                        No TDS entries found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
